/*!

=========================================================
* Argon Dashboard React - v1.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useState, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";

// reactstrap components
import {
  DropdownMenu,
  DropdownItem,
  Dropdown,
  DropdownToggle,
  Form,
  FormGroup,
  InputGroupText,
  Input,
  InputGroup,
  Navbar,
  Nav,
  Media,
} from "reactstrap";
import Preferences from "components/Forms/Preferences";
import keys from "configs/constants";
import { UpdateUserProfilePolicy, msalAuth } from "services/backendService";
import { bindActionCreators } from "redux";
import { setUser } from "@redux/actions/index";
import api from "../../services/backendService";
import { withTranslation } from "react-i18next";
import "./style.css";
import utils, { siteUniqueIdMatches } from "utils";
import { AuthorizationContext } from "../../contexts/AuthorizationContext";
import mapSearchItemsToProps, { isSearchableInScope, searchItemMatches } from "./mapSearchItemsToProps";
import { Store as notifyStore } from "react-notifications-component";
import { tempOptions, NotifyContent } from "components/Notify";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTheme } from "@/theme/ThemeContext";
import { THEME_DARK } from "@/theme/theme.utils";

const Name = ({ name, itemInfo, searchTerm, onSelect, t }) => {
  Name.propTypes = {
    name: PropTypes.string.isRequired,
    itemInfo: PropTypes.shape({
      type: PropTypes.string.isRequired,
      extra: PropTypes.string,
      cabinetSerial: PropTypes.string,
      siteInfo: PropTypes.object,
      gatewayInfo: PropTypes.object,
    }).isRequired,
    searchTerm: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
  };

  let itemTitle = {};
  let itemLink = "";
  let itemInfo_ = {};

  switch (itemInfo.type) {
    case "site":
      itemTitle = {
        title: name,
        icon: keys.ICON_SITE_MAP,
        // Only for a site found by its Site ID: it shows why the site is listed.
        subtitles: siteUniqueIdMatches(itemInfo, searchTerm)
          ? [{ text: itemInfo.uniqueId, prefix: t("siteProperties.uniqueSiteId") }]
          : undefined,
      };
      itemLink = "/std/dvcs";
      itemInfo_ = itemInfo;
      break;
    case "gateway":
      itemTitle = {
        title: name,
        icon: keys.ICON_GATEWAY,
        subtitles: [
          { text: itemInfo.extra ?? "" },
          {
            text: itemInfo.siteInfo.name,
            prefix: "Site",
          },
        ],
      };
      itemLink = "/std/dvcs";
      itemInfo_ = itemInfo.siteInfo;
      break;
    case "device":
      itemTitle = {
        title: name,
        icon: keys.ICON_CONTROLLER,
        subtitles: [
          {
            text: itemInfo.extra !== "" ? itemInfo.extra : undefined,
          },
          {
            text: itemInfo.gatewayInfo.name,
            prefix: "Gateway",
          },
          ...(itemInfo.cabinetSerial
            ? [
                {
                  text: itemInfo.cabinetSerial,
                  prefix: t("navBar.cabinetSerial"),
                },
              ]
            : []),
        ],
      };
      itemLink = "/std/dvcs";
      itemInfo_ = itemInfo.gatewayInfo.siteInfo;
      break;
  }

  return (
    <Link
      to={itemLink}
      state={{ site: itemInfo_ }}
      onClick={onSelect}
    >
      <div className="search-item">
        <div className="search-item-title">
          <span className="search-item-icon">{itemTitle.icon}</span>
          <span>{itemTitle.title}</span>
        </div>
        {itemTitle.subtitles?.map((subtitle, index) => (
          <div className="search-item-subtitle" key={index}>
            {subtitle.prefix && (
              <span className="search-item-subtitle-prefix">
                {subtitle.prefix}:{" "}
              </span>
            )}
            <span>{subtitle.text}</span>
          </div>
        ))}
      </div>
    </Link>
  );
};

export const SearchContainer = ({ items, searchTerm, onSelect, t }) => {
  SearchContainer.propTypes = {
    items: PropTypes.array.isRequired,
    searchTerm: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
  };

  return (
    <>
      {items.length === 0 ? (
        <div></div>
      ) : (
        <div className={`search-result${window.innerWidth < 767 ? "-sm" : ""}`}>
          {items.map((item) => (
            <Name
              key={item.guid}
              name={item.name}
              itemInfo={item}
              searchTerm={searchTerm}
              onSelect={onSelect}
              t={t}
            />
          ))}
        </div>
      )}
    </>
  );
};

export const ThinHeader = ({
  auth,
  searchItems,
  controllers,
  setUser,
  t,
  brandText,
  onSignOut,
}) => {
  const location = useLocation();
  const context = useContext(AuthorizationContext);
  const { effectiveTheme, toggleTheme } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  ThinHeader.propTypes = {
    auth: PropTypes.object.isRequired,
    searchItems: PropTypes.array.isRequired,
    controllers: PropTypes.object.isRequired,
    setUser: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    brandText: PropTypes.string.isRequired,
    onSignOut: PropTypes.func.isRequired,
  };

  const handleClick = (e) => {
    if (e.currentTarget.getAttribute("value") === "logout") {
      onSignOut();
    }
    if (e.currentTarget.getAttribute("value") === "profile") {
      msalAuth.loginRedirect({ authority: UpdateUserProfilePolicy });
    }
  };

  const onInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const dynamicSearch = () => {
    const term = searchTerm.trim().toLowerCase();
    if (term === "") return [];
    return searchItems.filter(
      (item) => isSearchableInScope(item, context) && searchItemMatches(item, term)
    );
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const togglePreferencesModal = (status = undefined) => {
    setIsPreferencesOpen(
      typeof status !== "boolean" ? !isPreferencesOpen : status
    );
  };

  const username = auth.user.idToken.name;

  const itemsFound = dynamicSearch();
  const activeOrganization = context.activeOrganization;
  // With the organization filter, the filtered organization for everyone,
  // super admins included; nothing with no filter.
  const showsOrganizationName = context.isOrgFilterEnabled
    ? !!context.filteredOrganization
    : !utils.isUserAdmin();
  const organizationName = (
    <>
      <span className="text-white">{keys.ICON_CUSTOMER}</span>
      <span className="ml-1 text-white">{activeOrganization ? activeOrganization.name : ""}</span>
    </>
  );
  const shouldHideSearch = !context.isPaidSubscription();
  const searchResults = shouldHideSearch ? [] : itemsFound;
  const nextThemeLabel =
    effectiveTheme === THEME_DARK
      ? t("websitePreferences.theme.toggleToLight")
      : t("websitePreferences.theme.toggleToDark");
  const toggleIconClass =
    effectiveTheme === THEME_DARK ? "fas fa-sun" : "fas fa-moon";

  // Export Summary logic
  const [isExporting, setIsExporting] = useState(false);
  const handleExportSummary = async () => {
    if (isExporting) return;
    setIsExporting(true);

    notifyStore.addNotification({
      ...tempOptions,
      dismiss: { duration: 10000 },
      content: NotifyContent(
        "success",
        t("summary.longProcessWarningBackground")
      ),
    });

    try {
      const exportResult = await api.exportOrganizationsSummary();
      if (!exportResult) {
        return;
      }

      const { blob, fileName } = exportResult;
      if (!blob) {
        return;
      }

      const urlCreator = globalThis.URL;
      const doc = globalThis.document;
      if (!urlCreator?.createObjectURL || !doc?.createElement || !doc.body) {
        return;
      }

      const url = urlCreator.createObjectURL(blob);
      const link = doc.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName || "summary.xlsx");
      doc.body.appendChild(link);
      link.click();
      link.remove();
      urlCreator.revokeObjectURL(url);
    } catch (error) {
      utils.debug(error, utils.MSG_TYPE_ERR);
      notifyStore.addNotification({
        ...tempOptions,
        content: NotifyContent("danger", t("summary.exportFail")),
      });
    } finally {
      setIsExporting(false);
    }
  };

  const getPageTitleFromPath = () => {
    const setPrefixUpperCase = (prefix) => t(prefix).toUpperCase();

    const path = location?.pathname || "";
    const defaultTitle = setPrefixUpperCase("sidebar." + brandText);

    const safeName = (val, fallback = defaultTitle) =>
      val && val !== "" ? val : fallback;

    if (path.includes("/std/dvcs")) {
      return `${setPrefixUpperCase("generic.site")}: ${safeName(location?.state?.site?.name)}`;
    }

    if (path.includes("/std/sv")) {
      const deviceGuid =
        location?.state?.device?.deviceGuid ??
        location?.state?.site?.deviceGuid;
      const device =
        deviceGuid && controllers?.byId ? controllers.byId[deviceGuid] : null;

      const deviceName = safeName(
        device?.friendlyName ||
          (device?.model ? utils.getModelFromMFVE(device.model) : null) ||
          location?.state?.device?.friendlyName ||
          location?.state?.device?.model ||
          location?.state?.device?.name,
        defaultTitle
      );
      const siteName = safeName(location?.state?.site?.siteInfo?.name);

      return `${setPrefixUpperCase("generic.site")}: ${siteName} - ${setPrefixUpperCase("generic.device")}: ${deviceName}`;
    }

    if (path.includes("/std/gtw")) {
      const siteName = safeName(location?.state?.site?.name);
      const gatewayName = safeName(location?.state?.gateway?.name);
      return `${setPrefixUpperCase("generic.edit")} ${setPrefixUpperCase("generic.gateway")}: ${gatewayName} - ${setPrefixUpperCase("generic.site")}: ${siteName}`;
    }

    if (path.includes("/std/sstgs")) {
      const siteName = safeName(location?.state?.site?.name);
      return `${setPrefixUpperCase("generic.edit")} ${setPrefixUpperCase("generic.site")}: ${siteName} `;
    }

    if (path.includes("/std/alrlg")) {
      const names = location?.state?.alarmLog?.selectedSiteNames || [];
      if (names.length === 1) return `${defaultTitle}: ${names[0]}`;
      if (names.length > 1) return `${defaultTitle}: ${setPrefixUpperCase("alarmLog.multipleSites")}`;
      return defaultTitle;
    }

    // The guide is reached from Access Rights, not from the sidebar, so there is
    // no sidebar entry to take a name from -- "sidebar.roles" would be a second
    // name for a page that already has one. This is the heading the page itself
    // shows, so the two cannot drift apart.
    if (path.includes("/std/roles")) {
      return setPrefixUpperCase("roles.rolesHeading");
    }

    return defaultTitle;
  };

  return (
    <>
      <Preferences
        isOpen={isPreferencesOpen}
        toggle={togglePreferencesModal}
        auth={auth}
        setUser={setUser}
        t={t}
      />
      <Navbar className="navbar-top navbar-dark" expand="md" id="navbar-main">
        {(() => {
          const path = location?.pathname || "";
          const titleClass =
            "h4 mb-0 text-white d-none d-lg-inline-block";
          if (path.includes("/std/alrlg")) {
            const sites = location?.state?.alarmLog?.selectedSites || [];
            const names = location?.state?.alarmLog?.selectedSiteNames || [];
            const clearBtn = (
              <button
                type="button"
                className="btn btn-link btn-sm text-white p-0 ml-2"
                title={t("alarmLog.clearSiteFilter")}
                onClick={() =>
                  globalThis.dispatchEvent(new Event("alarmLog:clearSiteFilter"))
                }
              >
                <i className="fas fa-times" />
              </button>
            );
            if (sites.length === 1) {
              const site = sites[0];
              return (
                <span className={`${titleClass} text-uppercase`}>
                  {t("sidebar." + brandText)}:{" "}
                  <Link
                    to="/std/dvcs"
                    state={{ site }}
                    className="text-white text-underline"
                  >
                    {site.name}
                  </Link>
                  {clearBtn}
                </span>
              );
            }
            if (names.length > 1) {
              return (
                <span className={`${titleClass} text-uppercase`}>
                  {t("sidebar." + brandText)}: {t("alarmLog.multipleSites")}
                  {clearBtn}
                </span>
              );
            }
          }
          return (
            <Link className={titleClass} to="/">
              {getPageTitleFromPath()}
            </Link>
          );
        })()}
        {window.location.pathname.includes("/ad") || shouldHideSearch ? null : (
          <>
            <Form className="navbar-search navbar-search-dark form-inline mr-3 d-none d-md-flex ml-lg-auto">
              <FormGroup className="mb-0">
                <InputGroup className="input-group-alternative">
                  <InputGroupText>{keys.ICON_SEARCH}</InputGroupText>
                  <Input
                    placeholder={t("sidebar.search")}
                    value={searchTerm}
                    onChange={onInputChange}
                    type="text"
                  />
                </InputGroup>
              </FormGroup>
            </Form>
            <SearchContainer
              items={searchResults}
              searchTerm={searchTerm}
              onSelect={clearSearch}
              t={t}
            />
          </>
        )}
        {/* Below lg the title is hidden, so space-between would leave this on
            the left on its own: push it right there. From lg the title and the
            search place it. */}
        <Nav className="align-items-center ml-auto ml-lg-0" navbar data-testid="header-user-nav">
          <button
            type="button"
            className="header-theme-toggle"
            onClick={toggleTheme}
            aria-label={nextThemeLabel}
            title={nextThemeLabel}
          >
            <i className={toggleIconClass} aria-hidden="true" />
          </button>
          <LanguageSwitcher />
          <Dropdown
            className="d-flex align-items-center flex-column"
            isOpen={isOpen}
            toggle={toggleMenu}
          >
            <DropdownToggle className="pr-0" nav>
              <Media className="pr-0 d-flex align-items-center flex-column">
                <Media className="text-center ml-2 d-none d-md-block">
                  <span className="mb-0 text-sm font-weight-bold">
                    {username}
                  </span>
                  <br />
                  <div className="text-center">
                    {utils.isUserAdmin() && (
                      <span className="badge bg-danger text-white">
                        SuperAdmin
                      </span>
                    )}
                  </div>
                </Media>
              </Media>
            </DropdownToggle>
            {/* Under the user name, or a super admin's badge. */}
            {showsOrganizationName && (
              <div
                className="text-center mt-1 d-none d-md-block"
                data-testid="header-organization"
                title={activeOrganization?.name}
              >
                {organizationName}
              </div>
            )}
            <DropdownMenu className="dropdown-menu-arrow dropdown-user" end>
              <DropdownItem className="noti-title" header tag="div">
                <h6 className="text-overflow m-0">
                  {t("navbar.welcome") + "!"}
                </h6>
              </DropdownItem>
              <DropdownItem>
                <div value="profile" onClick={handleClick}>
                  <span className="btn-inner--icon">
                    {keys.ICON_USERPROFILE}
                  </span>
                  <span className="ml-2 btn-inner--text">
                    {t("navbar.myProfile")}
                  </span>
                </div>
              </DropdownItem>
              {/* Preferences */}
              <div
                className="dropdown-item"
                value="preferences"
                onClick={togglePreferencesModal}
              >
                <span className="btn-inner--icon">{keys.ICON_PREFERENCES}</span>
                <span className="ml-2 btn-inner--text cursor-default">
                  {t("navbar.preferences")}
                </span>
              </div>
              <DropdownItem divider />
              {utils.isUserAdmin() && (
                <>
                  <DropdownItem>
                    <a
                      href={window.location.origin + "/ddf"}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span className="btn-inner--icon">
                        {keys.ICON_DEVICE_DESCRIPTOR}
                      </span>
                      <span className="ml-2 btn-inner--text cursor-default">
                        Device Description File Manager
                      </span>
                    </a>
                  </DropdownItem>
                  <DropdownItem
                    onClick={handleExportSummary}
                    disabled={isExporting}
                  >
                    <span className="btn-inner--icon">{keys.ICON_REPORTS}</span>
                    <span className="ml-2 btn-inner--text cursor-default">
                      {t("navbar.exportSummary")}
                    </span>
                  </DropdownItem>
                </>
              )}
              <DropdownItem>
                <div value="logout" onClick={handleClick}>
                  <span className="btn-inner--icon">{keys.ICON_LOGOUT}</span>
                  <span className="ml-2 btn-inner--text">
                    {t("navbar.logout")}
                  </span>
                </div>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </Nav>
      </Navbar>
    </>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setUser,
    },
    dispatch
  );

export default withTranslation("common")(
  connect(mapSearchItemsToProps, mapDispatchToProps)(ThinHeader)
);
